import { useState } from "react";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import {
  LoadingState,
  ErrorState,
  EmptyState,
  StrengthBar,
  AttentionBadge,
  RecencyBadge,
  AskAboutThis,
  supportTier,
  formatDay,
  pct,
} from "@/components/research-hub";
import { useCoverageGaps, type CoverageGapRow } from "@/hooks/useResearchHubCio";

type Row = Record<string, any>;

// "Whole book" (null) plus the five portfolios. Stored in state and passed to the hook.
const PORTFOLIO_CHIPS: { value: number | null; label: string }[] = [
  { value: null, label: "Whole book" },
  { value: 1, label: "1" },
  { value: 2, label: "2" },
  { value: 3, label: "3" },
  { value: 4, label: "4" },
  { value: 5, label: "5" },
];

export default function CioCoverage() {
  const [portfolio, setPortfolio] = useState<number | null>(null);
  const { data, isLoading, error, refetch } = useCoverageGaps(portfolio, 50);

  // The list is already ranked by the RPC (worst coverage first). We never show the
  // gap score itself — the *order* is the ranking — but we can normalise a "blind spot"
  // bar by rank so the first row reads strongest and it eases off down the list.
  const rows: CoverageGapRow[] = Array.isArray(data) ? data : [];
  const total = rows.length;
  const bookDate = rows.find((r) => r?.effective_date)?.effective_date;

  return (
    <div className="space-y-6">
      <header className="space-y-2">
        <h1 className="font-heading text-xl font-semibold">Research blind spots</h1>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <p className="font-body text-sm text-muted-foreground">
            Holdings the house view barely covers — worst first.
          </p>
          {bookDate && (
            <span className="font-body text-xs text-muted-foreground whitespace-nowrap">
              book as of {formatDay(bookDate)}
            </span>
          )}
        </div>

        {/* Portfolio filter chips */}
        <div className="flex flex-wrap gap-2 pt-1">
          {PORTFOLIO_CHIPS.map((chip) => {
            const active = chip.value === portfolio;
            return (
              <button
                key={chip.label}
                type="button"
                onClick={() => setPortfolio(chip.value)}
                aria-pressed={active}
                className={cn(
                  "rounded-full border px-3 py-1 font-body text-xs font-medium transition-colors",
                  active
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border text-muted-foreground hover:text-foreground hover:bg-muted/60",
                )}
              >
                {chip.value == null ? chip.label : `Portfolio ${chip.value}`}
              </button>
            );
          })}
        </div>
      </header>

      {isLoading ? (
        <LoadingState rows={4} />
      ) : error ? (
        <ErrorState onRetry={() => refetch()} />
      ) : total === 0 ? (
        <EmptyState title="No notable blind spots — coverage is solid." tone="good" />
      ) : (
        <div className="space-y-2">
          {rows.map((g: Row, i: number) => (
            <CoverageRow key={g.fund_id ?? `${g.fund_label ?? "fund"}-${i}`} g={g} rank={i} total={total} />
          ))}
        </div>
      )}
    </div>
  );
}

function CoverageRow({ g, rank, total }: { g: Row; rank: number; total: number }) {
  const tier = supportTier(g.support_tier, g.no_source);
  const fundLabel = g.fund_label ?? "A fund";
  // Rank-normalised blind-spot strength: 1 for the worst row, easing down the list.
  const blindSpot = total > 1 ? (total - rank) / total : 1;
  const prefill = `Why is ${fundLabel} under-researched?`;

  return (
    <Card className="p-3 flex items-start justify-between gap-3">
      <div className="min-w-0 flex-1 space-y-1.5">
        <div className="font-body text-sm">
          <span className="truncate">{fundLabel}</span>
          {g.portfolio != null && (
            <span className="text-muted-foreground"> (portfolio {g.portfolio})</span>
          )}
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <StrengthBar value={blindSpot} label="blind spot" width="w-20" />
          {g.weight_pct != null && (
            <span className="font-body text-xs text-muted-foreground">
              {pct(g.weight_pct)} of the book
            </span>
          )}
          {g.newest_source_at ? (
            <RecencyBadge date={g.newest_source_at} />
          ) : (
            <span className="font-body text-[11px] text-muted-foreground">no research on file</span>
          )}
        </div>
      </div>
      <div className="flex flex-col items-end gap-1.5 shrink-0">
        <AttentionBadge label={tier.label} tone={tier.tone} />
        <AskAboutThis prefill={prefill} label="Ask about this" />
      </div>
    </Card>
  );
}
